// ©2024 Austin App House. All rights reserved.
import React, { useContext, useEffect, useState } from 'react';
import { MDBCol, MDBIcon, MDBRow } from 'mdbreact';
import axios from 'axios';
import {connect} from 'react-redux';
import { toggleModal } from '../../../storage/actions/modalsActions';
import DashboardModalLoader from '../../dashboard/common/DashboardModalLoader';
import SelectMessageRecipients from './SelectMessageRecipients';
import NewMessage from './NewMessage';
// eslint-disable-next-line import/no-cycle
import MessageList from './MessageList';
// eslint-disable-next-line import/no-cycle
import { SocketContext } from '../../../pages';

const update = (data, handler) => {
  handler((state) => Array.from(new Set([...state, data])));
};

const WorkOrderMessageSystemModal = ({
  modalState,
  toggleModal,
  auth,
  applicantsLoaded,
  routedApplicantsLoaded,
  applicants,
  routedApplicants,
  activeTab
}) => {
  const [messages, fetchMessages] = useState([]);
  const [companyUsers, fetchCompanyUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isForContractor, setIsNeedSendToContractor] = useState(false);
  const [needScrollToBottom, scrollToBottom] = useState(false);
  const [selectedAllApplicants, setSelectedAllApplicants] = useState(false);

  const { chatSocket: socket } = useContext(SocketContext) ?? {};

  const fetchConversation = (socket) => {
    fetchMessages([]);
    socket && socket.emit('receiveHistory', { workOrderId: modalState.type?.workOrder?._id });
    socket && socket.on('history', (data) => {
      data.messages && fetchMessages(data.messages);
    });
  };

  useEffect(() => {
    modalState.type?.workOrder?._id && socket && fetchConversation(socket);
    modalState.type?.workOrder?._id && axios.get('/api/users/get-clients').then(res => {
      fetchCompanyUsers(
        res.data.clients
          .filter(el => el.userId)
          .map(el => ({ value: el.userId, label: el.name })),
      );
    });
    socket && socket.emit('openChat', { workOrderId: modalState.type?.workOrder?._id });
    socket && socket.on('reply', () => fetchConversation(socket));
    socket && socket.on(modalState.type?.workOrder?._id, message => {
      update(message, fetchMessages);
      scrollToBottom(true);
    });
  }, [modalState.type?.workOrder, modalState]);

  const scrollToBottomHandler = (ref) => {
    scrollToBottom(false);
    // eslint-disable-next-line no-param-reassign
    ref.current.scrollTop = ref.current.scrollHeight;
  };

  const sendMessage = (message, parentId) => {
    if (!socket) return;
    socket.emit('chat', {
      parentId,
      message,
      selectedAllApplicants,
      workOrderId: modalState.type?.workOrder?._id,
      users: selectedUsers,
      isForContractor,
    });
    scrollToBottom(true);
  };

  const closeModal = () => {
    fetchMessages([]);
    toggleModal(false, 'workOrderMessageSystemModal', true);
  };

  const getRecipients = (users, isNeedSendToContractor) => {
    setSelectedUsers(users);
    setIsNeedSendToContractor(isNeedSendToContractor);
  };

  if (!modalState.isOpen) return null;

  const isDataLoading = !applicantsLoaded || !routedApplicantsLoaded;
  const isViewOnly = auth.user.userType === 'superAdmin';
  const workOrder = modalState.type?.workOrder;
  const subtitle = auth.user.userType === 'contractor'
    ? `Client: ${workOrder?.clientInfo?.name || workOrder?.companyNCR?.name}`
    : workOrder?.title;

  return (
    <div
      className="modal_block dashboard-modal dashboard-modal--centered modal_messages showed"
      role="presentation"
      onClick={closeModal}
    >
      <div
        className="modal_container"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal_head">
          <div className="modal_title">Send Message To</div>
          <div className="modal_subtitle">{subtitle}</div>
          <button
            type="button"
            className="close_btn"
            aria-label="close"
            onClick={closeModal}
          >
            ×
          </button>
        </div>
        <div className="modal_body wurqly-modal-scroll">
          {isDataLoading && <DashboardModalLoader />}
          {!isDataLoading && (
            <>
              <div className="info_block">
                <div className="block_label">{`WO ID: ${workOrder?.customTagId || ''}`}</div>
              </div>
              {isViewOnly && (
                <div className="block_label" style={{ color: '#e87b68', margin: '10px 0' }}>
                  View only — messages cannot be sent from a Super Admin account.
                </div>
              )}
              {!isViewOnly && auth.user.userType !== 'contractor' && (
                <SelectMessageRecipients
                  companyMembers={companyUsers}
                  activeTab={activeTab}
                  selectedAllApplicants={selectedAllApplicants}
                  setSelectedAllApplicants={setSelectedAllApplicants}
                  workOrder={workOrder}
                  getRecipients={getRecipients}
                  applicants={applicants}
                  routedApplicants={routedApplicants}
                />
              )}
              {!isViewOnly && <NewMessage sendMessage={sendMessage} />}
              <MessageList
                userId={auth.user.id}
                scrollToBottom={scrollToBottomHandler}
                needScrollToBottom={needScrollToBottom}
                sendMessage={sendMessage}
                fetchConversation={fetchConversation}
                messages={messages}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  toggleModal,
};

const mapStateToProps = state => ({
  auth: state.auth,
  modalState: state.modalState.workOrderMessageSystemModal,
  applicantsLoaded: state.workOrder.applicantsLoaded,
  activeTab: state.applicationState.activeTab,
  routedApplicantsLoaded: state.workOrder.routedApplicantsLoaded,
  applicants: state.workOrder.applicants,
  routedApplicants: state.workOrder.routedApplicants,
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkOrderMessageSystemModal);
